import React, { useEffect, useState } from 'react';
import { NavLink, Link, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { Home, Rocket, DollarSign, Truck, UtensilsCrossed, Users, TrendingUp, Star, Sparkles, FileUp, Settings, LogOut, ShieldCheck, ChevronsUpDown, Plug, Globe, Instagram, Facebook, BarChart3, PanelLeftClose, PanelLeft, LayoutGrid, X, Mail, ArrowLeft, ChevronLeft, MapPin } from 'lucide-react';
import { useT } from '../../i18n';
import { restFetch, LogoBadge } from './restui';
import { useOsTheme, ThemeToggle } from '../../os3/theme';
import { TextSizePill } from '../../os3/OsFooter';
import { applyTextZoom } from '../../components/TextSizeControl';
import { RestDemoTour } from './RestDemoTour';
import { ShareDemoModal } from './ShareDemoModal';
import { VipIdentityCard } from './VipIdentityCard';
import { PresentButton, PresentationMode, PROFILE_CHOICES, switchDemoProfile } from './RestPresent';
import { MenuGuide } from './MenuGuide';
import { EstimateDisclaimer } from '../../components/KitxensEffect';
import { LangSwitch } from '../../components/LangSwitch';

/* Restaurant AI OS — the customer's private operating system. Kitxens is invisible infrastructure. */

function relTime(iso, isEs) {
  if (!iso) return isEs ? 'pendiente' : 'pending';
  const mins = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  if (mins < 60) return isEs ? `hace ${mins} min` : `${mins} min ago`;
  const h = Math.round(mins / 60);
  if (h < 24) return isEs ? `hace ${h} h` : `${h}h ago`;
  const days = Math.round(h / 24);
  return isEs ? `hace ${days} d` : `${days}d ago`;
}

const NAV = [
  { to: '', icon: Home, en: 'Today', es: 'Hoy', end: true },
  { to: 'intelligence', icon: BarChart3, en: 'Intelligence', es: 'Intelligence' },
  { to: 'mission-control', icon: Rocket, en: 'Mission Control', es: 'Mission Control' },
  { to: 'revenue', icon: DollarSign, en: 'Revenue', es: 'Revenue' },
  { to: 'locations', icon: MapPin, en: 'Locations', es: 'Locales', multi: true },
  { to: 'delivery', icon: Truck, en: 'Delivery', es: 'Delivery' },
  { to: 'menu', icon: UtensilsCrossed, en: 'Menu Intelligence', es: 'Menu Intelligence' },
  { to: 'guests', icon: Users, en: 'Guests', es: 'Guests' },
  { to: 'marketing', icon: TrendingUp, en: 'Marketing', es: 'Marketing' },
  { to: 'reviews', icon: Star, en: 'Reviews', es: 'Reviews' },
  { to: 'ai-executive', icon: Sparkles, en: 'AI Executive', es: 'AI Executive' },
  { to: 'reports', icon: FileUp, en: 'Reports', es: 'Reports' },
  { to: 'connections', icon: Plug, en: 'Connections', es: 'Conexiones' },
  { to: 'settings', icon: Settings, en: 'Settings', es: 'Settings' },
];

const API = process.env.REACT_APP_BACKEND_URL;

const MOBILE_PRIMARY = ['', 'revenue', 'menu', 'ai-executive'];

function MobileNav({ isEs, multiLoc }) {
  const [more, setMore] = useState(false);
  const navItems = NAV.filter((n) => !n.multi || multiLoc);
  const primary = MOBILE_PRIMARY.map((to) => NAV.find((n) => n.to === to));
  return (
    <>
      {more && (
        <div className="lg:hidden fixed inset-0 z-[57] bg-black/50 backdrop-blur-sm" onClick={() => setMore(false)} data-testid="rest-mobile-more-overlay">
          <div className="absolute bottom-[76px] inset-x-3 rounded-3xl bg-white p-4 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <p className="text-[10px] uppercase tracking-[0.2em] text-gray-400 font-bold">{isEs ? 'Todos los espacios' : 'All workspaces'}</p>
              <button onClick={() => setMore(false)} className="p-1 text-gray-400" data-testid="rest-mobile-more-close"><X className="w-4 h-4" /></button>
            </div>
            <div className="grid grid-cols-3 gap-2">
              {navItems.map((n) => (
                <NavLink key={n.to} to={n.to === '' ? '/restaurant' : `/restaurant/${n.to}`} end={n.end} onClick={() => setMore(false)}
                  data-testid={`rest-mobile-more-${n.to || 'home'}`}
                  className={({ isActive }) => `flex flex-col items-center gap-1.5 rounded-2xl border px-2 py-3 text-center ${isActive ? 'border-[var(--rest-accent)] bg-[var(--rest-accent-soft)] text-[var(--rest-accent)]' : 'border-gray-100 text-gray-600'}`}>
                  <n.icon className="w-[18px] h-[18px]" />
                  <span className="text-[10.5px] font-semibold leading-tight">{isEs ? n.es : n.en}</span>
                </NavLink>
              ))}
            </div>
          </div>
        </div>
      )}
      <nav className="lg:hidden fixed bottom-0 inset-x-0 z-[58] bg-white border-t border-gray-200" data-testid="rest-mobile-nav"
        style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}>
        <div className="grid grid-cols-5">
          {primary.map((n) => (
            <NavLink key={n.to} to={n.to === '' ? '/restaurant' : `/restaurant/${n.to}`} end={n.end}
              data-testid={`rest-mobile-nav-${n.to || 'home'}`}
              className={({ isActive }) => `flex flex-col items-center gap-0.5 pt-2 pb-1.5 ${isActive ? 'text-[var(--rest-accent)]' : 'text-gray-400'}`}>
              <n.icon className="w-5 h-5" />
              <span className="text-[9.5px] font-bold">{n.to === 'ai-executive' ? 'AI' : n.to === 'menu' ? 'Menu' : (isEs ? n.es : n.en)}</span>
            </NavLink>
          ))}
          <button onClick={() => setMore((v) => !v)} data-testid="rest-mobile-nav-more"
            className={`flex flex-col items-center gap-0.5 pt-2 pb-1.5 ${more ? 'text-[var(--rest-accent)]' : 'text-gray-400'}`}>
            <LayoutGrid className="w-5 h-5" />
            <span className="text-[9.5px] font-bold">{isEs ? 'Más' : 'More'}</span>
          </button>
        </div>
      </nav>
    </>
  );
}

function WorkspaceSwitcher({ isEs, currentName, company, companyId }) {
  const [os, setOs] = useState(null);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    fetch(`${API}/api/os/me`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null)).then(setOs).catch(() => {});
  }, []);
  if (!os || (os.workspaces || []).length < 2) return null;
  const switchTo = async (w) => {
    if (w.requires_keyword) { window.location.href = '/hub'; return; }
    const r = await fetch(`${API}/api/os/enter`, { method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ workspace_id: w.id }) });
    if (r.ok) { const j = await r.json(); window.location.href = j.path; }
  };
  const siblings = companyId ? os.workspaces.filter((w) => w.company_id === companyId) : [];
  // Founders first: Kitxens Admin → Arepas ★ → Nina's ★ → the rest.
  const isFounder = (w) => w.name === 'Arepas Latin Cuisine' || w.name.includes('Nina');
  const wsRank = (w) => (w.name === 'Kitxens Admin' ? 0 : w.name.includes('Nina') ? 1 : w.name === 'Arepas Latin Cuisine' ? 2 : w.name === 'Casa Mia Trattoria' ? 3 : 4);
  const others = os.workspaces.filter((w) => !companyId || w.company_id !== companyId).slice().sort((a, b) => wsRank(a) - wsRank(b));
  const locLabel = (w) => (company && w.name.startsWith(company) ? w.name.replace(company, '').replace(/^[\s—-]+/, '') || w.name : w.name);
  return (
    <div className="mx-5 mb-3 relative" data-testid="rest-workspace-switcher">
      <button onClick={() => setOpen(!open)} data-testid="rest-ws-switcher-btn"
        className="w-full flex items-center justify-between rounded-xl border-2 border-[var(--rest-accent)] bg-[var(--rest-accent-soft)] px-3.5 py-2.5 text-left hover:opacity-90 transition-opacity">
        <span className="min-w-0 flex items-center gap-2">
          <span className="text-[15px]">🏢</span>
          <span className="min-w-0">
            <span className="block text-[13px] font-bold text-gray-900 truncate">{company || currentName}</span>
            {company && <span className="block text-[10.5px] text-gray-500 truncate">{locLabel({ name: currentName })}</span>}
          </span>
        </span>
        <ChevronsUpDown className="w-4 h-4 text-[var(--rest-accent)] shrink-0" />
      </button>
      {open && (
        <div className="absolute z-40 mt-1 w-full rounded-xl border border-gray-200 bg-white shadow-lg py-1" data-testid="rest-ws-switcher-menu">
          {siblings.length > 1 && (
            <>
              <p className="px-3 pt-2 pb-1 text-[9.5px] uppercase tracking-wider text-gray-400 font-bold">{company}</p>
              {siblings.map((w) => (
                <button key={w.id} onClick={() => switchTo(w)} data-testid={`rest-ws-item-${w.id.replace(':', '-')}`}
                  className="w-full text-left px-3 py-2 text-[12.5px] hover:bg-gray-50 transition-colors flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full shrink-0" style={{ background: w.color }} />
                  <span className={`truncate font-medium ${w.name === currentName ? 'text-[var(--rest-accent)] font-bold' : 'text-gray-700'}`}>{locLabel(w)}</span>
                  {w.name === currentName && <span className="ml-auto text-[9px] text-gray-400 uppercase font-bold">{isEs ? 'aquí' : 'here'}</span>}
                </button>
              ))}
              {others.length > 0 && <div className="my-1 border-t border-gray-100" />}
            </>
          )}
          {others.map((w) => (
            <button key={w.id} onClick={() => switchTo(w)} data-testid={`rest-ws-item-${w.id.replace(':', '-')}`}
              className="w-full text-left px-3 py-2 text-[12.5px] hover:bg-gray-50 transition-colors flex items-center gap-2">
              <span className="w-2 h-2 rounded-full shrink-0" style={{ background: w.color }} />
              <span className="truncate text-gray-700 font-medium">{w.name}</span>
              {isFounder(w) && <Star className="w-3 h-3 text-amber-400 fill-amber-400 shrink-0" data-testid="rest-ws-founder-star" title={isEs ? 'Restaurante fundador' : 'Founding restaurant'} />}
              {w.name === 'Kitxens Admin' && <Star className="w-3 h-3 shrink-0" style={{ color: '#F77F11', fill: '#F77F11' }} data-testid="rest-ws-admin-star" title="Kitxens Admin" />}
            </button>
          ))}
          <a href="/hub" className="block px-3 py-2 text-[11.5px] text-gray-400 hover:text-gray-700 border-t border-gray-100 transition-colors" data-testid="rest-ws-goto-hub">
            {isEs ? 'Ir a Home →' : 'Go to Home →'}
          </a>
        </div>
      )}
    </div>
  );
}

function MobileBrandSwitcher({ isEs, r, isDemo }) {
  const [open, setOpen] = useState(false);
  const [os, setOs] = useState(null);
  useEffect(() => {
    if (isDemo) return;
    fetch(`${API}/api/os/me`, { credentials: 'include' })
      .then((rr) => (rr.ok ? rr.json() : null)).then(setOs).catch(() => {});
  }, [isDemo]);
  const wsRank = (w) => (w.name === 'Kitxens Admin' ? 0 : w.name.includes('Nina') ? 1 : w.name === 'Arepas Latin Cuisine' ? 2 : w.name === 'Casa Mia Trattoria' ? 3 : 4);
  const wss = os ? (os.workspaces || []).slice().sort((a, b) => wsRank(a) - wsRank(b)) : [];
  const switchTo = async (w) => {
    if (w.requires_keyword) { window.location.href = '/hub'; return; }
    const rr = await fetch(`${API}/api/os/enter`, { method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ workspace_id: w.id }) });
    if (rr.ok) { const j = await rr.json(); window.location.href = j.path; }
  };
  const canSwitch = isDemo || wss.length > 1;
  return (
    <div className="lg:hidden min-w-0 relative">
      <button onClick={() => canSwitch && setOpen((o) => !o)} className="text-left min-w-0 flex items-center gap-1.5" data-testid="rest-mobile-brand">
        <span className="min-w-0">
          <span className="block text-[9px] uppercase tracking-[0.2em] text-[var(--rest-accent)] font-bold">Restaurant AI OS</span>
          <span className="block text-[14px] font-semibold text-gray-900 truncate">{r.name}</span>
        </span>
        {canSwitch && <ChevronsUpDown className="w-3.5 h-3.5 text-gray-400 shrink-0" />}
      </button>
      {open && (
        <div className="absolute z-[70] mt-1.5 w-64 max-w-[82vw] rounded-xl border border-gray-200 bg-white shadow-xl py-1 max-h-[62vh] overflow-y-auto" data-testid="rest-mobile-brand-menu">
          {isDemo ? (<>
            <p className="px-3 pt-2 pb-1 text-[9.5px] uppercase tracking-wider text-gray-400 font-bold">{isEs ? 'Cambiar de restaurante' : 'Switch restaurant'}</p>
            {PROFILE_CHOICES.slice(0, 8).map((p) => (
              <button key={p.key} onClick={() => { if (r.slug !== p.slug) switchDemoProfile(p.key); else setOpen(false); }}
                data-testid={`rest-mobile-profile-${p.key}`}
                className="w-full text-left px-3 py-2 text-[12.5px] hover:bg-gray-50 transition-colors flex items-center gap-2.5">
                <span className={`truncate font-medium ${r.slug === p.slug ? 'text-[var(--rest-accent)] font-bold' : 'text-gray-700'}`}>{isEs ? p.es : p.en}</span>
                {r.slug === p.slug && <span className="ml-auto text-[9px] text-gray-400 uppercase font-bold">{isEs ? 'aquí' : 'here'}</span>}
              </button>
            ))}
            <a href="/demo" className="block px-3 py-2 text-[11.5px] text-gray-400 hover:text-gray-700 border-t border-gray-100 transition-colors" data-testid="rest-mobile-back-start">
              {isEs ? '← Volver al principio' : '← Back to the start'}
            </a>
          </>) : (<>
            {wss.map((w) => (
              <button key={w.id} onClick={() => switchTo(w)} data-testid={`rest-mobile-ws-${w.id.replace(':', '-')}`}
                className="w-full text-left px-3 py-2 text-[12.5px] hover:bg-gray-50 transition-colors flex items-center gap-2">
                <span className="w-2 h-2 rounded-full shrink-0" style={{ background: w.color }} />
                <span className={`truncate font-medium ${w.name === r.name ? 'text-[var(--rest-accent)] font-bold' : 'text-gray-700'}`}>{w.name}</span>
                {(w.name === 'Arepas Latin Cuisine' || w.name.includes('Nina')) && <Star className="w-3 h-3 text-amber-400 fill-amber-400 shrink-0" />}
                {w.name === 'Kitxens Admin' && <Star className="w-3 h-3 shrink-0" style={{ color: '#F77F11', fill: '#F77F11' }} />}
              </button>
            ))}
            <a href="/hub" className="block px-3 py-2 text-[11.5px] text-gray-400 hover:text-gray-700 border-t border-gray-100 transition-colors" data-testid="rest-mobile-goto-hub">
              {isEs ? '← Volver al principio' : '← Back to Home'}
            </a>
          </>)}
        </div>
      )}
    </div>
  );
}

export default function RestLayout() {
  const { lang } = useT();
  const isEs = lang === 'es';
  const nav = useNavigate();
  const loc = useLocation();
  const [theme, setTheme] = useOsTheme();
  const [me, setMe] = useState(null);
  const [denied, setDenied] = useState(false);
  const [shareOpen, setShareOpen] = useState('');
  const [showWelcome, setShowWelcome] = useState(() => !localStorage.getItem('kx-demo-welcome'));
  // The welcome card never overstays: it bows out on its own after 10s.
  useEffect(() => {
    if (!showWelcome) return undefined;
    const t = setTimeout(() => { try { localStorage.setItem('kx-demo-welcome', '1'); } catch { /* noop */ } setShowWelcome(false); }, 10000);
    return () => clearTimeout(t);
  }, [showWelcome]);
  const [accentLive, setAccentLive] = useState(null);
  const [fontLive, setFontLive] = useState(null);
  const [navCollapsed, setNavCollapsed] = useState(() => localStorage.getItem('kx_rest_collapsed') === '1');
  const toggleNav = () => setNavCollapsed((v) => { localStorage.setItem('kx_rest_collapsed', v ? '0' : '1'); return !v; });
  const [presenting, setPresenting] = useState(false);

  // Mobile gesture: swipe right from the left edge → open Rachel (AI concierge)
  useEffect(() => {
    let sx = 0, sy = 0, edge = false;
    const ts = (e) => { const t = e.touches[0]; sx = t.clientX; sy = t.clientY; edge = sx < 28; };
    const te = (e) => {
      const t = e.changedTouches[0];
      if (edge && t.clientX - sx > 80 && Math.abs(t.clientY - sy) < 60) {
        window.dispatchEvent(new CustomEvent('kitxens:rachel-open'));
      }
    };
    window.addEventListener('touchstart', ts, { passive: true });
    window.addEventListener('touchend', te, { passive: true });
    return () => { window.removeEventListener('touchstart', ts); window.removeEventListener('touchend', te); };
  }, []);

  useEffect(() => {
    restFetch('/me').then(async (r) => {
      if (!r.ok) { setDenied(true); return; }
      const j = await r.json();
      if (j.role !== 'demo') { try { sessionStorage.removeItem('kx_rest_demo'); } catch { /* noop */ } }
      setMe(j);
    }).catch(() => setDenied(true));
  }, []);

  useEffect(() => {
    if (!me || me.role !== 'demo') return;
    try {
      // Arrived through a shared VIP link → the experience ALWAYS opens itself, every time.
      if (sessionStorage.getItem('kx_present_force') === '1') {
        sessionStorage.removeItem('kx_present_force');
        setPresenting(true);
        return;
      }
      if (localStorage.getItem('kx_present_auto') !== '1') {
        localStorage.setItem('kx_present_auto', '1');
        setPresenting(true); // first demo visit → the experience opens itself
      }
    } catch { /* noop */ }
  }, [me]);

  useEffect(() => { if (denied) nav('/restaurant/login', { replace: true }); }, [denied, nav]);
  useEffect(() => { applyTextZoom(localStorage.getItem('kx_text_zoom') || '100'); }, []);
  useEffect(() => {
    const h = (e) => { if (/^#[0-9A-Fa-f]{6}$/.test(e.detail || '')) setAccentLive(e.detail); };
    const hf = (e) => setFontLive(e.detail ?? '');
    window.addEventListener('kx-accent', h);
    window.addEventListener('kx-font', hf);
    return () => { window.removeEventListener('kx-accent', h); window.removeEventListener('kx-font', hf); };
  }, []);

  if (!me) return <div className="min-h-screen bg-[#FAFAF8]" />;
  const r = me.restaurant;
  const inst = r.instance || {};

  const logout = async () => { await restFetch('/logout', { method: 'POST' }); nav('/restaurant/login'); };
  const accent = accentLive || r.branding?.primary_color || '#F77F11';
  const brandFont = fontLive ?? (r.branding?.font || '');

  return (
    <div className="min-h-screen os3-scope bg-[#FAFAF8] text-gray-700 flex" style={{ fontFamily: `${brandFont ? `'${brandFont}', ` : ''}Inter, system-ui, sans-serif`, '--rest-accent': accent, '--rest-accent-soft': `${accent}14` }}>
      {!presenting && (me.role === 'demo' || (() => { try { return sessionStorage.getItem('kx_rest_demo') === '1'; } catch { return false; } })()) && <RestDemoTour isEs={isEs} multiLoc={(r.locations || []).length > 1} />}
      {!presenting && me.role === 'demo' && !showWelcome && <MenuGuide isEs={isEs} />}
      {/* Sidebar */}
      <aside className={`hidden lg:flex ${navCollapsed ? 'w-[64px]' : 'w-[250px]'} shrink-0 flex-col border-r border-gray-200 bg-white min-h-screen sticky top-0 max-h-screen overflow-y-auto transition-[width] duration-300`} data-testid="rest-sidebar">
        <Link to="/restaurant" className={`block pt-6 pb-4 hover:opacity-80 transition-opacity ${navCollapsed ? 'px-2' : 'px-5'}`} data-testid="rest-brand-home-link">
          {!navCollapsed && <p className="text-[10px] uppercase tracking-[0.25em] text-[var(--rest-accent)] font-bold">Restaurant AI OS</p>}
          <div className={`mt-2 flex items-center gap-2.5 ${navCollapsed ? 'justify-center mt-0' : ''}`}>
            {r.branding?.logo_url && !navCollapsed ? (
              <LogoBadge src={r.branding.logo_url} alt={r.name} className="w-11 h-11 rounded-full" testid="rest-brand-logo" />
            ) : (
              <span className="w-9 h-9 shrink-0 rounded-lg bg-[var(--rest-accent)] text-white font-bold text-[15px] flex items-center justify-center" data-testid="rest-brand-monogram">{r.name?.[0] || 'R'}</span>
            )}
            {!navCollapsed && (
            <div>
              <h2 className="text-[15px] font-semibold tracking-tight text-gray-900 leading-snug" data-testid="rest-brand-name">{r.name}</h2>
              {r.founding ? (
                <p className="text-[10px] font-bold text-amber-600" data-testid="rest-founding-badge" title={isEs ? 'Construyendo el Restaurant AI OS desde 2026' : 'Helping build Restaurant AI OS since 2026'}>⭐ Founding Restaurant</p>
              ) : (
                <p className="text-[10px] text-gray-400">{isEs ? 'Instancia privada' : 'Private Instance'}</p>
              )}
            </div>
            )}
          </div>
        </Link>
        {!navCollapsed && <WorkspaceSwitcher isEs={isEs} currentName={r.name} company={r.company_name} companyId={r.company_id} />}
        {!navCollapsed && (
        <div className="mx-5 mb-4 rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5 space-y-1.5" data-testid="rest-instance-card">
          {[['Instance', <span className="font-mono font-bold text-gray-700">{inst.os_id || '—'}</span>],
            [isEs ? 'Environment' : 'Environment', <span className="inline-flex items-center gap-1 text-emerald-600 font-semibold capitalize"><span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />{inst.environment || 'production'}</span>],
            ['AI Status', <span className="text-gray-700 font-semibold">{isEs ? 'Conectada' : 'Connected'} · v{inst.ai_version || '2.1'}</span>],
            [isEs ? 'Last Sync' : 'Last Sync', <span className="text-gray-700 font-semibold">{relTime(me.last_sync, isEs)}</span>],
            ['Health', <span className="text-emerald-600 font-semibold">{isEs ? 'Operativa' : 'Operational'}</span>],
          ].map(([k, v], i) => (
            <div key={i} className="flex items-center justify-between text-[10px]">
              <span className="text-gray-400 uppercase tracking-wider">{k}</span>
              {v}
            </div>
          ))}
        </div>
        )}
        <nav className={`flex-1 space-y-0.5 ${navCollapsed ? 'px-2' : 'px-3'}`}>
          {NAV.filter((n) => !n.multi || (r.locations || []).length > 1).map((n) => (
            <NavLink key={n.to} to={n.to === '' ? '/restaurant' : `/restaurant/${n.to}`} end={n.end}
              data-testid={`rest-nav-${n.to || 'home'}`} title={isEs ? n.es : n.en}
              className={({ isActive }) => `flex items-center gap-3 rounded-lg px-3 py-2 text-[13px] font-medium transition-colors ${navCollapsed ? 'justify-center px-0' : ''} ${isActive ? 'bg-[var(--rest-accent-soft)] text-[var(--rest-accent)]' : 'text-gray-500 hover:text-gray-900 hover:bg-[var(--rest-accent-soft)]'}`}>
              <n.icon className="w-4 h-4 shrink-0" /> {!navCollapsed && (isEs ? n.es : n.en)}
            </NavLink>
          ))}
        </nav>
        <div className={`py-3 border-t border-gray-200 ${navCollapsed ? 'px-2' : 'px-4'}`}>
          <div className={`flex items-center ${navCollapsed ? 'justify-center' : 'justify-between'}`}>
            {!navCollapsed && (
              <div className="flex items-center gap-3" data-testid="rest-social-links">
                {r.branding?.social?.website && <a href={r.branding.social.website} target="_blank" rel="noreferrer" title="Web" aria-label="Website" data-testid="rest-social-web" className="text-gray-400 hover:text-[var(--rest-accent)] transition-colors"><Globe className="w-4 h-4" /></a>}
                {r.branding?.social?.instagram && <a href={r.branding.social.instagram} target="_blank" rel="noreferrer" title="Instagram" aria-label="Instagram" data-testid="rest-social-instagram" className="text-gray-400 hover:text-[var(--rest-accent)] transition-colors"><Instagram className="w-4 h-4" /></a>}
                {r.branding?.social?.facebook && <a href={r.branding.social.facebook} target="_blank" rel="noreferrer" title="Facebook" aria-label="Facebook" data-testid="rest-social-facebook" className="text-gray-400 hover:text-[var(--rest-accent)] transition-colors"><Facebook className="w-4 h-4" /></a>}
                {r.branding?.social?.tiktok && (
                  <a href={r.branding.social.tiktok} target="_blank" rel="noreferrer" title="TikTok" aria-label="TikTok" data-testid="rest-social-tiktok" className="text-gray-400 hover:text-[var(--rest-accent)] transition-colors">
                    <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4" aria-hidden="true"><path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/></svg>
                  </a>
                )}
              </div>
            )}
            <button onClick={toggleNav} data-testid="rest-sidebar-toggle"
              title={navCollapsed ? (isEs ? 'Expandir' : 'Expand') : (isEs ? 'Contraer' : 'Collapse')}
              className="flex items-center justify-center rounded-lg px-2.5 py-2 text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors">
              {navCollapsed ? <PanelLeft className="w-4 h-4" /> : <PanelLeftClose className="w-4 h-4" />}
            </button>
          </div>
          {!navCollapsed && (
            <a href="https://www.kitxens.com" target="_blank" rel="noreferrer" data-testid="rest-powered-by"
              className="mt-3 pb-1 flex items-center justify-center gap-1.5 opacity-75 hover:opacity-100 transition-opacity">
              <span className="text-[10px] text-gray-400">Powered by</span>
              <img src="/kitxens-favicon.png" alt="Kitxens" className="w-[15px] h-[15px] rounded-[4px]" />
              <span className="text-[10.5px] font-bold text-[var(--rest-accent)] tracking-tight">Kitxens</span>
            </a>
          )}
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 min-w-0">
        {me.impersonated && (
          <div className="bg-[#F77F11] text-black text-[12px] font-bold px-5 py-2 flex items-center justify-between" data-testid="rest-impersonation-banner">
            <span className="inline-flex items-center gap-2"><ShieldCheck className="w-4 h-4" /> {isEs ? `Modo Kitxens Admin — viendo ${r.name} exactamente como el cliente` : `Kitxens Admin mode — experiencing ${r.name} exactly as the customer`}</span>
            <a href="/admin/ai-os/restaurants" className="underline">{isEs ? 'Volver al AI OS' : 'Back to AI OS'}</a>
          </div>
        )}
        {me.role === 'demo' && (
          <div className="bg-[#1A1A1A] text-white text-[12px] px-5 py-2 flex items-center justify-between gap-3" data-testid="rest-demo-banner">
            <span className="inline-flex items-center gap-2 min-w-0">
              <span className="shrink-0 rounded-full bg-[var(--rest-accent)] text-white text-[9.5px] font-bold uppercase tracking-wider px-2 py-0.5">Demo</span>
              <span className="truncate text-white/80">
                <span className="sm:hidden">{isEs ? 'Datos de muestra' : 'Sample data'}</span>
                <span className="hidden sm:inline">{isEs ? 'Estás explorando el Restaurant AI OS con datos de muestra — este mismo sistema funcionará con TUS datos.' : 'You are exploring the Restaurant AI OS with sample data — this exact system runs on YOUR data.'}</span>
              </span>
            </span>
            <span className="shrink-0 flex items-center gap-3 sm:gap-4">
              <span className="hidden md:inline-flex items-center gap-0.5 rounded-full border border-white/15 p-0.5" data-testid="rest-demo-profile-switch"
                title={isEs ? 'Cambia el tipo de cocina de la demo' : 'Switch the demo cuisine'}>
                {PROFILE_CHOICES.map((p) => (
                  <button key={p.key} onClick={() => { if (r.slug !== p.slug) switchDemoProfile(p.key); }}
                    data-testid={`rest-demo-profile-${p.key}`} title={isEs ? p.es : p.en}
                    className={`inline-flex items-center rounded-full px-2 py-0.5 transition-all ${r.slug === p.slug ? 'bg-[var(--rest-accent)]' : 'opacity-60 hover:opacity-100'}`}>
                    <span className={`text-[10.5px] font-bold ${r.slug === p.slug ? 'text-white' : 'text-white/70'}`}>{isEs ? p.es : p.en}</span>
                  </button>
                ))}
              </span>
              <button onClick={() => setShareOpen('self')}
                className="sm:hidden text-[var(--rest-accent)] leading-none" aria-label={isEs ? 'Envíatelo' : 'Email me the link'}
                data-testid="rest-demo-sendme-mobile">
                <Mail className="w-4 h-4" />
              </button>
              <button onClick={() => setShareOpen('self')}
                className="hidden sm:inline-flex items-center gap-1.5 text-[var(--rest-accent)] hover:brightness-110 transition-all font-bold" data-testid="rest-demo-sendme">
                <Mail className="w-3.5 h-3.5" /> {isEs ? 'Envíatelo' : 'Email me the link'}
              </button>
              <button onClick={() => setShareOpen('share')}
                className="hidden sm:inline text-white/60 hover:text-white transition-colors font-semibold" data-testid="rest-demo-share">
                {isEs ? 'Compartir demo' : 'Share demo'}
              </button>
              <a href="/demo" className="font-bold text-[var(--rest-accent)] hover:underline" data-testid="rest-demo-cta">{isEs ? 'Quiero el mío →' : 'Make it mine →'}</a>
            </span>
          </div>
        )}
        {shareOpen && <ShareDemoModal isEs={isEs} mode={shareOpen} onClose={() => setShareOpen('')}
          profileKey={(PROFILE_CHOICES.find((p) => p.slug === r.slug) || {}).key || ''} />}
        {me.role === 'demo' && <VipIdentityCard isEs={isEs} />}
        {me.role === 'demo' && showWelcome && !presenting && (
          <div className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 pointer-events-none" data-testid="rest-demo-welcome">
            <div className="bg-white rounded-3xl p-8 w-full max-w-lg text-center pointer-events-auto">
              <p className="text-[10px] uppercase tracking-[0.3em] text-[var(--rest-accent)] font-bold">Restaurant AI OS · Live Demo</p>
              <h2 className="mt-3 text-[22px] font-semibold text-gray-900">{isEs ? 'Esto no es una demo grabada.' : 'This is not a recorded demo.'}</h2>
              <p className="mt-2 text-[14px] text-gray-500">{isEs ? `Es el producto real funcionando con un restaurante de muestra: ${r.name}.` : `It is the real product running on a sample restaurant: ${r.name}.`}</p>
              <div className="mt-5 space-y-3 text-left">
                {[[isEs ? 'Tu OS ya analizó el negocio' : 'Your OS already analyzed the business', isEs ? 'El Home te recibe con prioridades, clima y noticias reales.' : 'Home greets you with priorities, live weather and real news.'],
                  [isEs ? 'Números reales, cero inventos' : 'Real numbers, zero fakes', isEs ? 'Mission Control y Revenue muestran 90 días de datos con su fuente.' : 'Mission Control and Revenue show 90 days of data with its source.'],
                  [isEs ? 'Tu AI Executive' : 'Your AI Executive', isEs ? 'Genera un brief ejecutivo con juicio basado en los datos.' : 'Generates an executive brief with data-grounded judgment.']].map(([t, s], i) => (
                    <div key={t} className="flex gap-3 items-start">
                      <span className="w-6 h-6 rounded-full bg-[var(--rest-accent)] text-white text-[12px] font-bold flex items-center justify-center shrink-0">{i + 1}</span>
                      <div><p className="text-[13.5px] font-semibold text-gray-900">{t}</p><p className="text-[12px] text-gray-500">{s}</p></div>
                    </div>
                  ))}
              </div>
              <button onClick={() => { localStorage.setItem('kx-demo-welcome', '1'); setShowWelcome(false); }} data-testid="rest-demo-welcome-start"
                className="mt-6 w-full rounded-xl bg-[var(--rest-accent)] hover:opacity-90 text-white font-bold text-[14.5px] py-3 transition-opacity shadow-md">
                {isEs ? 'Empezar a explorar →' : 'Start exploring →'}
              </button>
            </div>
          </div>
        )}
        <header className="flex items-center justify-between gap-2 px-3 sm:px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-2 min-w-0">
            <button onClick={() => window.history.back()} data-testid="rest-nav-back" aria-label={isEs ? 'Atrás' : 'Back'}
              title={isEs ? 'Volver atrás' : 'Go back'}
              className="w-8 h-8 rounded-full border border-gray-200 bg-white flex items-center justify-center text-gray-400 hover:text-gray-800 hover:border-gray-300 transition-colors shrink-0">
              <ChevronLeft className="w-4 h-4" />
            </button>
            <MobileBrandSwitcher isEs={isEs} r={r} isDemo={me.role === 'demo'} />
          </div>
          <div className="hidden lg:block" />
          <div className="flex items-center gap-2 sm:gap-4 shrink-0">
            <PresentButton isEs={isEs} onStart={() => setPresenting(true)} />
            <span className="hidden md:inline-flex"><TextSizePill isEs={isEs} compact /></span>
            <LangSwitch />
            <ThemeToggle theme={theme} setTheme={setTheme} isEs={isEs} />
            <span className="hidden lg:inline text-[12.5px] text-gray-500" data-testid="rest-user-name">{me.user?.name || me.user?.email}</span>
            <button onClick={logout} data-testid="rest-logout-btn" aria-label={isEs ? 'Salir' : 'Sign out'}
              className="inline-flex items-center gap-1.5 text-[12px] text-gray-400 hover:text-gray-900 transition-colors">
              <LogOut className="w-3.5 h-3.5" /> <span className="hidden sm:inline">{isEs ? 'Salir' : 'Sign out'}</span>
            </button>
          </div>
        </header>
        <main className="px-4 py-6 pb-24 sm:px-6 sm:py-7 lg:pb-8 max-w-[1200px] overflow-x-hidden kx-zoomable" style={typeof window !== 'undefined' && window.innerWidth >= 1024 ? { zoom: 1.08 } : undefined}>
          <div key={loc.pathname} style={{ animation: 'kx-view-in 0.55s cubic-bezier(0.22,0.9,0.3,1) both' }}>
            <Outlet context={{ me, isEs }} />
            <EstimateDisclaimer isEs={isEs} />
          </div>
          <style>{'@keyframes kx-view-in { 0% { opacity: 0; transform: translateY(14px); } 100% { opacity: 1; transform: translateY(0); } }'}</style>
        </main>
        <MobileNav isEs={isEs} multiLoc={(r.locations || []).length > 1} />
        <PresentationMode isEs={isEs} active={presenting} onExit={() => setPresenting(false)}
          demoSlug={me.role === 'demo' ? r.slug : null} multiLoc={(r.locations || []).length > 1} />
      </div>
    </div>
  );
}
